import { useState } from 'react';
import { Modal, Button, Textarea, Select, StatusBadge, Spinner, Alert } from '@00start/ui';
import { useIncidentDetail } from '@/hooks/useIncidentDetail';
import { useAuth } from '@/hooks/useAuth';
import { postJSON } from '@/api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ActionKey = 'contain' | 'escalate' | 'rca' | 'rca/reject';

const VALID_ACTIONS = new Set<ActionKey>(['contain', 'escalate', 'rca', 'rca/reject']);

const SEVERITY_OPTIONS = [
  { value: 'P0', label: 'P0 — Critical' },
  { value: 'P1', label: 'P1 — High' },
  { value: 'P2', label: 'P2 — Medium' },
  { value: 'P3', label: 'P3 — Low' },
  { value: 'P4', label: 'P4 — Informational' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IncidentDetailModalProps {
  incidentId: string | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function severityColorKey(severity: string): string {
  if (severity === 'P0' || severity === 'P1') return 'critical';
  if (severity === 'P2') return 'high';
  return 'low';
}

function formatTs(ts: string | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function parseContributingFactors(raw: unknown): string[] | null {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      // Not valid JSON — return as single-item array
      return [raw];
    }
    return [raw];
  }
  return null;
}

// ---------------------------------------------------------------------------
// Sub-forms
// ---------------------------------------------------------------------------

interface ContainFormProps {
  incidentId: string;
  token: string | null;
  onSuccess: () => void;
}

function ContainForm({ incidentId, token, onSuccess }: ContainFormProps) {
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    const action: ActionKey = 'contain';
    if (!VALID_ACTIONS.has(action)) return;
    setSubmitting(true);
    setError(null);
    try {
      await postJSON(`/api/incidents/${incidentId}/contain`, token, { description });
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3 pt-2">
      {error && <Alert variant="danger">{error}</Alert>}
      <Textarea
        label="Containment description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe the containment action taken…"
        rows={3}
      />
      <Button variant="primary" onClick={handleSubmit} disabled={submitting || !description.trim()}>
        {submitting ? 'Submitting…' : 'Contain'}
      </Button>
    </div>
  );
}

interface EscalateFormProps {
  incidentId: string;
  token: string | null;
  onSuccess: () => void;
}

function EscalateForm({ incidentId, token, onSuccess }: EscalateFormProps) {
  const [severity, setSeverity] = useState('P1');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    const action: ActionKey = 'escalate';
    if (!VALID_ACTIONS.has(action)) return;
    setSubmitting(true);
    setError(null);
    try {
      await postJSON(`/api/incidents/${incidentId}/escalate`, token, { severity, reason });
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3 pt-2">
      {error && <Alert variant="danger">{error}</Alert>}
      <Select
        label="New severity"
        options={SEVERITY_OPTIONS}
        value={severity}
        onChange={(e) => setSeverity(e.target.value)}
      />
      <Textarea
        label="Escalation reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Explain why escalation is necessary…"
        rows={3}
      />
      <Button variant="danger" onClick={handleSubmit} disabled={submitting || !reason.trim()}>
        {submitting ? 'Escalating…' : 'Escalate'}
      </Button>
    </div>
  );
}

interface RcaFormProps {
  incidentId: string;
  token: string | null;
  onSuccess: () => void;
}

function RcaForm({ incidentId, token, onSuccess }: RcaFormProps) {
  const [rootCause, setRootCause] = useState('');
  const [contributingFactors, setContributingFactors] = useState('');
  const [mitigations, setMitigations] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    const action: ActionKey = 'rca';
    if (!VALID_ACTIONS.has(action)) return;
    setSubmitting(true);
    setError(null);
    try {
      await postJSON(`/api/incidents/${incidentId}/rca`, token, {
        root_cause: rootCause,
        contributing_factors: contributingFactors,
        mitigations,
      });
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3 pt-2">
      {error && <Alert variant="danger">{error}</Alert>}
      <Textarea
        label="Root cause"
        value={rootCause}
        onChange={(e) => setRootCause(e.target.value)}
        placeholder="Describe the root cause…"
        rows={3}
      />
      <Textarea
        label="Contributing factors"
        value={contributingFactors}
        onChange={(e) => setContributingFactors(e.target.value)}
        placeholder="List contributing factors (one per line or JSON array)…"
        rows={3}
      />
      <Textarea
        label="Mitigations"
        value={mitigations}
        onChange={(e) => setMitigations(e.target.value)}
        placeholder="Describe mitigations applied or planned…"
        rows={3}
      />
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={submitting || !rootCause.trim()}
      >
        {submitting ? 'Submitting…' : 'Submit RCA'}
      </Button>
    </div>
  );
}

interface RcaRejectFormProps {
  incidentId: string;
  token: string | null;
  onSuccess: () => void;
}

function RcaRejectForm({ incidentId, token, onSuccess }: RcaRejectFormProps) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    const action: ActionKey = 'rca/reject';
    if (!VALID_ACTIONS.has(action)) return;
    setSubmitting(true);
    setError(null);
    try {
      await postJSON(`/api/incidents/${incidentId}/rca/reject`, token, { reason: rejectionReason });
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3 pt-2">
      {error && <Alert variant="danger">{error}</Alert>}
      <Textarea
        label="Rejection reason"
        value={rejectionReason}
        onChange={(e) => setRejectionReason(e.target.value)}
        placeholder="Explain why the RCA is being rejected…"
        rows={3}
      />
      <Button
        variant="danger"
        onClick={handleSubmit}
        disabled={submitting || !rejectionReason.trim()}
      >
        {submitting ? 'Rejecting…' : 'Reject'}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function IncidentDetailModal({ incidentId, onClose }: IncidentDetailModalProps) {
  const { token } = useAuth();
  const { incident, loading, reload } = useIncidentDetail(incidentId);
  const [activeAction, setActiveAction] = useState<ActionKey | null>(null);

  function toggleAction(key: ActionKey) {
    setActiveAction((prev) => (prev === key ? null : key));
  }

  function handleActionSuccess() {
    setActiveAction(null);
    reload();
  }

  const title = incidentId
    ? `Incident ${incidentId.length > 20 ? incidentId.slice(0, 20) + '…' : incidentId}`
    : 'Incident Detail';

  return (
    <Modal isOpen={!!incidentId} onClose={onClose} title={title} size="lg">
      <div data-testid="ops-incident-detail-modal">
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="sm" />
        </div>
      ) : !incident ? (
        <div className="py-4 text-sm text-slate-400">Incident not found.</div>
      ) : (
        <div className="space-y-5">

          {/* --- Summary --- */}
          <div className="flex flex-wrap gap-4 text-[12px]">
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Severity</span>
              <StatusBadge
                label={incident.severity}
                colorKey={severityColorKey(incident.severity)}
                variant="severity"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Status</span>
              <span className="text-slate-300 capitalize">{incident.status}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Trigger</span>
              <span className="text-slate-300">{incident.trigger_type}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Opened</span>
              <span className="text-slate-400">{formatTs(incident.opened_at)}</span>
            </div>
          </div>

          {/* --- Timeline --- */}
          {incident.timeline && incident.timeline.length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Timeline</h3>
              <ul className="space-y-2">
                {incident.timeline.map((event, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[12px]">
                    <div className="mt-0.5 shrink-0">
                      <StatusBadge
                        label={event.status ?? event.type ?? 'event'}
                        colorKey="low"
                        variant="status"
                      />
                    </div>
                    <div className="min-w-0">
                      <span className="text-slate-400 font-mono text-[10px]">
                        {formatTs(event.timestamp ?? event.occurred_at)}
                      </span>
                      {event.message && (
                        <p className="text-slate-300 mt-0.5 break-words">{event.message}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* --- RCA (if present) --- */}
          {incident.rca && (
            <div className="border border-slate-800 rounded p-3 space-y-2 text-[12px]">
              <h3 className="text-[11px] uppercase tracking-wide text-slate-500">RCA</h3>
              {incident.rca.root_cause && (
                <div>
                  <span className="text-slate-500 block mb-0.5">Root cause</span>
                  <p className="text-slate-300">{incident.rca.root_cause}</p>
                </div>
              )}
              {incident.rca.contributing_factors != null && (() => {
                const factors = parseContributingFactors(incident.rca!.contributing_factors);
                return factors && factors.length > 0 ? (
                  <div>
                    <span className="text-slate-500 block mb-0.5">Contributing factors</span>
                    <ul className="list-disc list-inside text-slate-300 space-y-0.5">
                      {factors.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                ) : null;
              })()}
              {incident.rca.mitigations && (
                <div>
                  <span className="text-slate-500 block mb-0.5">Mitigations</span>
                  <p className="text-slate-300">{incident.rca.mitigations}</p>
                </div>
              )}
            </div>
          )}

          {/* --- Actions --- */}
          <div className="border-t border-slate-800 pt-4">
            <h3 className="text-[11px] uppercase tracking-wide text-slate-500 mb-3">Actions</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                data-testid="ops-incident-action-contain"
                variant={activeAction === 'contain' ? 'primary' : 'secondary'}
                onClick={() => toggleAction('contain')}
              >
                Contain
              </Button>
              <Button
                data-testid="ops-incident-action-escalate"
                variant={activeAction === 'escalate' ? 'danger' : 'secondary'}
                onClick={() => toggleAction('escalate')}
              >
                Escalate
              </Button>
              <Button
                data-testid="ops-incident-action-rca"
                variant={activeAction === 'rca' ? 'primary' : 'secondary'}
                onClick={() => toggleAction('rca')}
              >
                Submit RCA
              </Button>
              <Button
                data-testid="ops-incident-action-rca-reject"
                variant={activeAction === 'rca/reject' ? 'danger' : 'secondary'}
                onClick={() => toggleAction('rca/reject')}
              >
                Reject RCA
              </Button>
            </div>

            {activeAction === 'contain' && (
              <ContainForm
                incidentId={incident.id}
                token={token}
                onSuccess={handleActionSuccess}
              />
            )}
            {activeAction === 'escalate' && (
              <EscalateForm
                incidentId={incident.id}
                token={token}
                onSuccess={handleActionSuccess}
              />
            )}
            {activeAction === 'rca' && (
              <RcaForm
                incidentId={incident.id}
                token={token}
                onSuccess={handleActionSuccess}
              />
            )}
            {activeAction === 'rca/reject' && (
              <RcaRejectForm
                incidentId={incident.id}
                token={token}
                onSuccess={handleActionSuccess}
              />
            )}
          </div>

        </div>
      )}
      </div>
    </Modal>
  );
}
